//-------------------------------------------------------------------------
// ZoneDevicePanel
//-------------------------------------------------------------------------
// Author: Robert Rolland (robert.rolland@nuvoola.com)
// Date:   Tuesday, July 12, 2022
//-------------------------------------------------------------------------
// This componnent is used to encapsulate the creation and maintenance of
// LUKE devices it is intended to be a drop in panel for the devices tab
// of a zone.

import React, { useState, useEffect } from "react";

import { connect } from "react-redux";
import update from "immutability-helper";
import { get, map, size, debounce, filter } from "lodash";

import Grid from "@material-ui/core/Grid";
import { withStyles } from "@material-ui/styles";
import { I18n } from "@aws-amplify/core";

import NuvTabPanel from "../Common/NuvTabPanel";
import PaperLightGrey from "../Common/PaperLightGrey";
import NuvButton from "../Common/NuvButton";
import Loader from "../Common/Loader";
import CreateDialog from "../Common/CreateDialog";

import ZoneDeviceForm from "./ZoneDeviceForm";

import colors from "../../assets/style/colors";
import { ReactComponent as CirclePlusIcon } from "../../assets/img/common/plus_icon.svg";
import { ReactComponent as EditIcon } from "../../assets/img/common/edit_icon.svg";
import { ReactComponent as SaveIcon } from "../../assets/img/common/save_base.svg";
import { ReactComponent as CancelIcon } from "../../assets/img/common/close_base.svg";
import { ReactComponent as DeleteIcon } from "../../assets/img/common/delete_base.svg";

import { error, success } from "../../constants/common";
import { fetchGet, fetchPut, fetchPost } from "../../api/fetch";
import { fetchDataStoreKey } from "../../utils/datastore";
import { Dialog } from "@material-ui/core";
import EditWarning from "../Zones/EditWarning";

const styles = {
  deviceHeightWithSpacer: {
    height: "calc(100vh - 247px)",
    overflow: "auto"
  },
  deviceHeightWithoutSpacer: {
    height: "calc(100vh - 207px)",
    overflow: "auto"
  }
};

/** ZoneDevicePanel
 * Note:
 *      Currently there are two levels in this control since for the moment
 * we will only support a single device per zone on this interface.  The
 * ability to handle multiple devices per zone in the VMI section will be
 * supported in the future.  At that point the interface for a single
 * device should be separated from the list of devices.
 */
const ZoneDevicePanel = (props) => {
  const { classes,
    value,
    companyId,
    siteId,
    zoneId,
    setZoneDeviceCount,
    hasZoneSpacer,
    setEditMode,
    isEditMode,
    language
  } = props;
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  // const [isEditMode, setEditMode] = useState(false);
  const [isFetchingDevices, setIsFetchingDevices] = useState(false);
  const [isFetchingDevice, setIsFetchingDevice] = useState(false);
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState();
  const [device, setDevice] = useState();
  const [deviceAttr, setDeviceAttr] = useState();
  const [arcturusService, setArcturusService] = useState();
  const [arcturusCloud, setArcturusCloud] = useState();
  const [values, setValues] = useState();
  const [status, setStatus] = useState(); // Not sure what this is used for yet in the create dialog.
  const [valid, setValid] = useState();
  const [errors, setErrors] = useState({});

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    fetchArcturusConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchZoneDevices(zoneId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoneId]);

  useEffect(() => {
    // Note if there is any devices, fetch the first one
    // Should only be one for now.
    if (devices && devices.length > 0) {
      let deviceId = devices[0]['id'];
      setDeviceId(deviceId);
      fetchDevice(deviceId);
    } else {
      setDeviceId();
      setDevice();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices]);

  useEffect(() => {
    if (device) {
      buildValues(device);
    } else {
      setValues({});
      setDeviceAttr();
      validateValues({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device]);

  const buildValues = async (device) => {
    let newValues = {
      name: get(device, ["name"]),
      device_type_id: get(device, ["device_type_id"]),
      device_type_name_en: get(device, ["device_type_name_en"]),
      device_type_name_fr: get(device, ["device_type_name_fr"]),
      attributes: get(device, ["attributes"]),
      parent_zone_id: get(device, ["parent_zone_id"]),
      sys_color_id:
        get(device, ["color", "id"]) || get(device, ["type", "color", "id"]),
    };
    setValues(newValues);
    validateValues(newValues);
  };

  const reset = async () => {
    setEditMode(false);
    // setEditDevice(false);
    await fetchZoneDevices(zoneId);
  };

  const fetchZoneDevices = async (zoneId) => {
    try {
      if (!zoneId) return;
      setIsFetchingDevices(true);
      let res = await fetchGet(
        `zones/${zoneId}/devices?active=true`
      );
      setDevices([
        ...res
      ]);
      if (res) {
        setZoneDeviceCount(res.length);
      } else {
        setZoneDeviceCount(0);
      }
      return res;
    } catch (e) {
      console.log(e);
    } finally {
      setIsFetchingDevices(false);
    }
  };

  const fetchDevice = async (deviceId) => {
    try {
      if (!deviceId) {
        setDevice();
        setDeviceAttr();
        return;
      }
      setIsFetchingDevice(true);
      let res = await fetchGet(
        `devices/${deviceId}`
      );
      setDevice(res);
      fetchDeviceAttr(get(res, ["device_type_id"]))
    } catch (e) {
      console.log(e);
    } finally {
      setIsFetchingDevice(false);
    }
  };

  const fetchDeviceAttr = async (typeId) => {
    setDeviceAttr(await fetchGet(`device_types/${typeId}/attributes`));
  };

  const fetchArcturusConfigs = async () => {
    try {
      let [
        serviceJson,
        cloudJson,
      ] = await Promise.all([
        fetchDataStoreKey("arcturus.vending.machine.service"),
        fetchDataStoreKey("arcturus.cloud")
      ]);
      setArcturusService(serviceJson);
      setArcturusCloud(cloudJson);
    } catch (e) {
      console.log(e);
    }
  };

  const handleAddDevice = () => {
    setStatus();
    setValues({});
    setEditMode(true);
    // setEditDevice(true)
    setCreateDialogOpen(true);
  };

  const handleCancelAddDevice = () => {
    setErrors({});
    setDevice();
    setDeviceAttr();
    if (device) {
      buildValues(device);
    }
    setEditMode(false)
    // setEditDevice(false)
    setCreateDialogOpen(false);
  };

  const handleEditDevice = async () => {
    setEditMode(true);
    // setEditDevice(true);
  };

  const handleCreateDevice = async () => {
    try {
      let url = `companies/${companyId}/sites/${siteId}/zones/${zoneId}/devices`;
      await fetchPost(url, values);
      setStatus(success);
    } catch (e) {
      console.log(e);
      setStatus(error);
    }
  };

  const handleSaveDevice = async () => {
    try {
      let url = `companies/${get(props, ["companyId"])}/sites/${get(props, [
        "siteId",
      ])}/zones/${get(props, ["zoneId"])}/devices/${get(device, ["id"])}`;
      await fetchPut(url, values);

      setStatus(success);
    } catch (e) {
      console.log(e);
      setStatus(error);
    }
    reset();
  };

  const handleCancelDevice = () => {
    // setStatus();
    setErrors({});
    setEditMode(false);
    // setEditDevice(false);
  };

  const handleDeleteDevice = async () => {
    try {
      let newValues = values;
      newValues['active'] = false;
      let url = `companies/${get(props, ["companyId"])}/sites/${get(props, [
        "siteId",
      ])}/zones/${get(props, ["zoneId"])}/devices/${get(device, ["id"])}`;
      await fetchPut(url, values);

      setStatus(success);
    } catch (e) {
      console.log(e);
      setStatus(error);
    }
    reset();
  };

  const handleChange = (id, index, value) => {
    if (
      isNaN(parseInt(id)) &&
      id.includes("type_id") &&
      !isNaN(parseInt(value))
    ) {
      fetchDeviceAttr(parseInt(value));
    }
    let newValues;
    if (!isNaN(parseInt(id))) {
      let newAttr = !get(values, ["attributes"])
        ? [{ id, value }]
        : size(
            filter(
              get(values, ["attributes"]),
              (attr) => get(attr, ["id"]) === id
            )
          ) === 0
        ? update(get(values, ["attributes"]), { $push: [{ id, value }] })
        : map(get(values, ["attributes"]), (attr) =>
            get(attr, ["id"]) === id ? { ...attr, value } : attr
          );
      newValues = update(values, {
        attributes: { $set: newAttr },
      });
    } else {
      newValues = update(values, {
        [id]: { $set: value },
      });
    }
    validation(newValues, id);
    setValues(newValues);
  };

  const validateValues = async (values) => {
    let newValid = {
      name: false,
      device_type_id: false,
    };
    let newErrors = {};
    let fields = Object.keys(values);
    for (let i = 0; i < size(fields); i++) {
      if (fields[i] === "attributes") {
        for (let j = 0; j < size(get(values, ["attributes"])); j++) {
          [newValid, newErrors] = await validateField(
            values,
            newValid,
            newErrors,
            get(values, ["attributes", j, "id"])
          );
        }
      } else {
        [newValid, newErrors] = await validateField(
          values,
          newValid,
          newErrors,
          fields[i]
        );
      }
    }
    setValid(newValid);
    setErrors(newErrors);
  };

  const validation = (newFormValues, field) => {
    debouncedValidation(newFormValues, valid, errors, field);
  };

  const debouncedValidation = React.useCallback(
    debounce(async (newFormValues, newValid, newErrors, field) => {
      if (field) {
        [newValid, newErrors] = await validateField(
          newFormValues,
          newValid,
          newErrors,
          field
        );
        setValid(newValid);
        setErrors(newErrors);
        return newValid;
      }
    }, 500),
    []
  );

  const validateField = async (newValues, newValid, newErrors, id) => {
    let isValid = false;
    let error;
    if (
      ["name"].includes(id) ||
      (isNaN(parseInt(id)) && id.includes("type_id"))
    ) {
      if (get(newValues, [id]) === "") {
        error = I18n.get("users_requiredField");
      } else {
        isValid = true;
      }
    } else {
      let attr = get(
        filter(deviceAttr, (attr) => attr.id === id),
        0
      );
      if (
        (get(attr, ["required"]) && !get(newValues, ["id"])) ||
        get(newValues, ["id"]) === ""
      ) {
        isValid = false;
        error = I18n.get("users_requiredField");
      } else {
        isValid = true;
      }
    }
    return [
      update(newValid, {
        [id]: { $set: isValid },
      }),
      update(newErrors, {
        [id]: { $set: error },
      }),
    ];
  };

  return (
    <NuvTabPanel value={value}>
      <CreateDialog
        open={createDialogOpen}
        onClose={handleCancelAddDevice}
        dlgTitle={I18n.get("companies_deviceNew")}
        typeName={I18n.get("companies_device")}
        creationSuccessMsg={I18n.get("companies_createdSuccessfully")}
        creationFailedMsg={I18n.get("companies_creationFailed")}
        validForm={size(filter(valid, (v) => !v)) === 0}
        values={values}
        status={status}
        reset={reset}
        submit={handleCreateDevice}
        columns={
          size(
            filter(
              deviceAttr,
              (attr) => attr.active && !attr.app_display_main_page
            )
          ) > 0
            ? 2
            : 1
        }
      >
        <ZoneDeviceForm
          isEditMode={isEditMode}
          handleChange={handleChange}
          values={values}
          valid={valid}
          arcturusCloud={arcturusCloud}
          arcturusService={arcturusService}
          deviceAttr={deviceAttr}
          isDialog={true}
        />
      </CreateDialog>

      <Dialog open={deleteDialogOpen} onClose={() => { setDeleteDialogOpen(false)}}>
        
          <EditWarning
          language={language}
          handleNo={() => { setDeleteDialogOpen(false) }}
          handleClose={() => { setDeleteDialogOpen(false) }}
          message={"device_delete"}
          handleRowClick={() => { setDeleteDialogOpen(false); setEditMode(false); handleDeleteDevice()}}
          warningType={"delete"}

          />
      </Dialog>

      <PaperLightGrey elevation={0} className={hasZoneSpacer ? classes.deviceHeightWithSpacer : classes.deviceHeightWithoutSpacer}>
        <Grid container direction="column" justify="flex-start" wrap="nowrap" style={{ height: "100%" }}>
          {isFetchingDevices
            ?
            <Grid item align="center" style={{ height: "100%" }}>
              <Loader noDecoration />
            </Grid>
            :
            <Grid item>
              {/* Action bar */}
              {!deviceId
                ?
                <Grid container direction="row" justify="flex-start" style={{ padding: "8px 12px" }}>
                  <Grid item>
                    <NuvButton onClick={() => { handleAddDevice() }} startIcon={<CirclePlusIcon />}>
                      <span style={{ textTransform: "uppercase" }}>{I18n.get("common_device_add")}</span>
                    </NuvButton>
                  </Grid>
                </Grid>
                :
                <Grid container direction="row" style={{ padding: "8px 12px" }}>
                  <Grid item xs={6} style={{ alignSelf: "center", fontSize: "1.8em" }}>
                    {get(device, "name")}
                  </Grid>
                  <Grid item xs={6} container direction="row" justify="flex-end">
                    {isEditMode
                      ?
                      <>
                        <Grid item>
                          <NuvButton 
                            onClick={() => { handleSaveDevice() }}
                            disabled={size(filter(valid, (v) => !v)) > 0}
                          >
                            <SaveIcon />
                          </NuvButton>
                        </Grid>
                        <Grid item>
                          <NuvButton style={{ color: colors.black, }} onClick={() => { handleCancelDevice() }}>
                            <CancelIcon />
                          </NuvButton>
                        </Grid>
                        <Grid item>
                          <NuvButton style={{ color: colors.redFlag, }} onClick={() => { setDeleteDialogOpen(true); /*handleDeleteDevice()*/ }}>
                            <DeleteIcon />
                          </NuvButton>
                        </Grid>
                      </>
                      :
                      <Grid item>
                        <NuvButton onClick={() => { handleEditDevice() }}>
                          <EditIcon />
                        </NuvButton>
                      </Grid>
                    }
                  </Grid>
                </Grid>
              }
              {/* Device fields */}
              {!device
                ?
                isFetchingDevice &&
                <Grid item align="center" style={{ height: "100%" }}>
                  <Loader noDecoration />
                </Grid>
                :
                <ZoneDeviceForm
                  isEditMode={isEditMode}
                  handleChange={handleChange}
                  values={values}
                  valid={valid}
                  arcturusCloud={arcturusCloud}
                  arcturusService={arcturusService}
                  deviceAttr={deviceAttr}
                  isDialog={false}
                />
              }
            </Grid>
          }
        </Grid>
      </PaperLightGrey>
    </NuvTabPanel>
  );
};

const mapStateToProps = (state) => {
  return {
    language: get(state, ["user", "language"])
  };
};

export default connect(mapStateToProps)(withStyles(styles)(ZoneDevicePanel));
